import {
	SET_OPPORTUNITIES_SETTINGS,
	SET_OPPORTUNITY_STATUS_LABEL,
	SET_ALL_OPPORTUNITY_STATUS_LABELS,
	SET_OPPORTUNITIES_LOADING,
	SET_OPPORTUNITIES_ERROR,
} from '@/actions/types';
import type {
	OpportunitiesSettings,
	OpportunityStatusLabelMap,
} from '@/types/models/opportunities';

const defaultStatusLabels: Record<string, string> = {
	Interested: 'Interested',
	'In communication': 'In communication',
	'Follow up': 'Follow up',
	Won: 'Won',
	Closed: 'Closed',
};

type OpportunitiesState = {
	statusLabels: OpportunityStatusLabelMap;
	loading: boolean;
	error: string | null;
	settings?: OpportunitiesSettings;
};

const initialState: OpportunitiesState = {
	statusLabels: { ...defaultStatusLabels },
	loading: false,
	error: null,
	settings: undefined,
};

// Note: explicit Action union intentionally removed to avoid unused type warning

export default function opportunitiesReducer(
	state: OpportunitiesState = initialState,
	action: { type: string; payload?: unknown }
) {
	switch (action.type) {
		case SET_OPPORTUNITIES_LOADING: {
			const loading = Boolean(action.payload);
			return { ...state, loading };
		}
		case SET_OPPORTUNITIES_ERROR: {
			const error =
				typeof action.payload === 'string' ? action.payload : null;
			return { ...state, error, loading: false };
		}
		case SET_OPPORTUNITIES_SETTINGS: {
			const settings = (action.payload || {}) as OpportunitiesSettings;
			let incomingLabels: OpportunityStatusLabelMap =
				((settings &&
					(settings as unknown as { statusLabels?: unknown })
						.statusLabels) as
					| OpportunityStatusLabelMap
					| undefined) || {};
			// Normalize possible Map to plain object
			const maybeMap = incomingLabels as unknown as Map<string, string>;
			if (
				maybeMap &&
				typeof maybeMap.entries === 'function' &&
				typeof maybeMap.get === 'function'
			) {
				incomingLabels = Object.fromEntries(maybeMap.entries());
			}
			return {
				...state,
				settings,
				statusLabels: {
					...defaultStatusLabels,
					...(typeof incomingLabels === 'object'
						? incomingLabels
						: {}),
				},
				loading: false,
				error: null,
			};
		}
		case SET_ALL_OPPORTUNITY_STATUS_LABELS: {
			const labels = (action.payload || {}) as OpportunityStatusLabelMap;
			return {
				...state,
				statusLabels: {
					...defaultStatusLabels,
					...(typeof labels === 'object' ? labels : {}),
				},
			};
		}
		case SET_OPPORTUNITY_STATUS_LABEL: {
			const payload = (action.payload || {}) as {
				status?: string;
				label?: string;
			};
			if (!payload.status) return state;
			return {
				...state,
				statusLabels: {
					...state.statusLabels,
					[payload.status]:
						payload.label ||
						defaultStatusLabels[payload.status] ||
						payload.status,
				},
			};
		}
		default:
			return state;
	}
}

export { defaultStatusLabels };
